import { RouteRecordRaw } from 'vue-router';
import { PageName } from '@/router/routes/constants';
import { Permission } from '@/project/auth/permission.service';
import dictionaryService from '@/core/translation/dictionary.service';

export const MyAccountRoutes: RouteRecordRaw =
{
    path: '/my-account',
    name: PageName.MY_ACCOUNT,
    redirect: { name: PageName.MY_ACCOUNT_PROFILE },
    component: () => import(
        '@/project/app/client-pages/my-account/MyAccountPage.vue'),
    meta: {
        isPublic: false,
        breadcrumb: [
            {
                name: dictionaryService.get('MyAccount.Navigation.Title'),
                page: PageName.MY_ACCOUNT
            }
        ]
    },
    children: [
        {
            path: 'profile',
            name: PageName.MY_ACCOUNT_PROFILE,
            component: () => import(
                '@/project/app/client-pages/my-account/profile/MyAccountProfile.vue'),
            meta: {
                permission: Permission.VIEW_MYACCOUNT,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Profile'),
                        page: PageName.MY_ACCOUNT_PROFILE
                    }
                ]
            }
        },
        {
            path: 'product-backlog',
            name: PageName.MY_ACCOUNT_PRODUCT_BACKLOG,
            component: () => import(
                '@/project/app/client-pages/my-account/product-backlog/ProductBacklog.vue'),
            meta: {
                permission: Permission.VIEW_ORDER_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductBacklog'),
                        page: PageName.MY_ACCOUNT_PRODUCT_BACKLOG
                    }
                ]
            }
        },
        {
            path: 'order-history',
            name: PageName.MY_ACCOUNT_ORDER_HISTORY,
            component: () => import(
                '@/project/app/client-pages/my-account/order/OrderHistory.vue'),
            meta: {
                permission: Permission.VIEW_ORDER_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.OrderHistory'),
                        page: PageName.MY_ACCOUNT_ORDER_HISTORY
                    }
                ]
            }
        },
        {
            path: 'order-history/detail/:orderId/:quoteId?',
            name: PageName.MY_ACCOUNT_ORDER_HISTORY_DETAIL,
            component: () => import(
                '@/project/app/client-pages/my-account/order/OrderDetailVersionSwitch.vue'),
            props: true,
            meta: {
                permission: Permission.VIEW_ORDER_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.OrderHistory'),
                        page: PageName.MY_ACCOUNT_ORDER_HISTORY
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.OrderDetail'),
                        page: PageName.MY_ACCOUNT_ORDER_HISTORY_DETAIL
                    }
                ]
            }
        },
        {
            path: 'quotes',
            name: PageName.MY_ACCOUNT_QUOTES,
            component: () => import(
                '@/project/app/client-pages/my-account/quote/Quotes.vue'),
            meta: {
                permission: Permission.VIEW_QUOTE_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Quotes'),
                        page: PageName.MY_ACCOUNT_QUOTES
                    }
                ]
            }
        },
        {
            path: 'quotes/detail/:quoteId',
            name: PageName.MY_ACCOUNT_QUOTES_DETAIL,
            component: () => import(
                '@/project/app/client-pages/my-account/quote/QuotesDetail.vue'),
            props: true,
            meta: {
                permission: Permission.VIEW_QUOTE_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Quotes'),
                        page: PageName.MY_ACCOUNT_QUOTES
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.QuotesDetail'),
                        page: PageName.MY_ACCOUNT_QUOTES_DETAIL
                    }
                ]
            }
        },
        {
            path: 'bids',
            name: PageName.MY_ACCOUNT_BIDS,
            component: () => import(
                '@/project/app/client-pages/my-account/bids/Bids.vue'),
            meta: {
                permission: Permission.VIEW_BID_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Bids'),
                        page: PageName.MY_ACCOUNT_BIDS
                    }
                ]
            }
        },
        {
            path: 'blanket-orders',
            name: PageName.MY_ACCOUNT_BLANKET_ORDERS,
            component: () => import(
                '@/project/app/client-pages/my-account/blanket/Blankets.vue'),
            meta: {
                permission: Permission.VIEW_QUOTE_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.BlanketOrders'),
                        page: PageName.MY_ACCOUNT_BLANKET_ORDERS
                    }
                ]
            }
        },
        {
            path: 'blanket-orders/detail/:orderId',
            name: PageName.MY_ACCOUNT_BLANKET_DETAIL,
            component: () => import(
                '@/project/app/client-pages/my-account/blanket/BlanketsDetail.vue'),
            props: (route) => ({
                orderId: route.params.orderId
            }),
            meta: {
                permission: Permission.VIEW_QUOTE_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.BlanketOrders'),
                        page: PageName.MY_ACCOUNT_BLANKET_ORDERS
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.BlanketOrdersDetail'),
                        page: PageName.MY_ACCOUNT_BLANKET_DETAIL
                    }
                ]
            }
        },
        {
            path: 'rma-returns',
            name: PageName.MY_ACCOUNT_RMA_RETURN,
            component: () => import('@/project/app/client-pages/my-account/rma-returns/RmaReturnsFrontPage.vue'),
            meta: {
                permission: Permission.VIEW_RMA_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN
                    }
                ]
            }
        },
        {
            path: 'rma-returns/create/:rmaCreateType?',
            name: PageName.MY_ACCOUNT_RMA_RETURN_CREATE,
            component: () => import('@/project/app/client-pages/my-account/rma-returns/RmaReturnsCreatePage.vue'),
            props: (route) => ({
                rmaCreateType: route.params.rmaCreateType
            }),
            meta: {
                permission: Permission.CREATE_RMA,
                skipAutoPageTracking: true,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN
                    },
                    {
                        name: dictionaryService.get('MyAccount.RmaReturns.CreateRma'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN_CREATE
                    }
                ]
            }
        },
        {
            path: 'rma-returns/history',
            name: PageName.MY_ACCOUNT_RMA_RETURN_HISTORY,
            component: () => import('@/project/app/client-pages/my-account/rma-returns/RmaReturnsHistoryPage.vue'),
            meta: {
                permission: Permission.VIEW_RMA_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN
                    },
                    {
                        name: dictionaryService.get('MyAccount.RmaReturns.History'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN_HISTORY
                    }
                ]
            }
        },
        {
            path: 'rma-returns/history/:rmaId',
            name: PageName.MY_ACCOUNT_RMA_RETURN_HISTORY_DETAIL,
            component: () => import(
                '@/project/app/client-pages/my-account/rma-returns/RmaReturnsHistoryPage.vue'),
            props: (route) => ({
                rmaId: route.params.rmaId
            }),
            meta: {
                permission: Permission.VIEW_RMA_STATUS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN
                    },
                    {
                        name: dictionaryService.get('MyAccount.RmaReturns.History'),
                        page: PageName.MY_ACCOUNT_RMA_RETURN_HISTORY
                    }
                ]
            }
        },
        {
            path: 'ledger',
            name: PageName.MY_ACCOUNT_LEDGER,
            component: () => import(
                '@/project/app/client-pages/my-account/ledger/Ledger.vue'),
            meta: {
                permission: Permission.VIEW_STATEMENT_OF_ACCOUNT,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Ledger'),
                        page: PageName.MY_ACCOUNT_LEDGER
                    }
                ]
            }
        },
        {
            path: 'services',
            name: PageName.MY_ACCOUNT_SERVICES,
            component: () => import('@/project/app/client-pages/my-account/services/Services.vue'),
            meta: {
                permission: Permission.VIEW_SERVICES,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Services'),
                        page: PageName.MY_ACCOUNT_SERVICES
                    }
                ]
            }
        },
        {
            path: 'favourites',
            name: PageName.MY_ACCOUNT_FAVOURITES,
            component: () => import(
                '@/project/app/client-pages/my-account/favourites/Favourites.vue'),
            meta: {
                permission: Permission.VIEW_FAVOURITES,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Favourites'),
                        page: PageName.MY_ACCOUNT_FAVOURITES
                    }
                ]
            }
        },
        {
            path: 'favourites/detail/:listId',
            name: PageName.MY_ACCOUNT_FAVOURITES_DETAIL,
            component: () => import(
                '@/project/app/client-pages/my-account/favourites/FavouritesDetail.vue'),
            props: (route) => ({
                listId: route.params.listId
            }),
            meta: {
                permission: Permission.VIEW_FAVOURITES,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Favourites'),
                        page: PageName.MY_ACCOUNT_FAVOURITES
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.FavouritesDetail'),
                        page: PageName.MY_ACCOUNT_FAVOURITES_DETAIL
                    }
                ]
            }
        },
        {
            path: 'settings',
            name: PageName.MY_ACCOUNT_ACCOUNT_SETTINGS,
            component: () => import(
                '@/project/app/client-pages/my-account/settings/AccountSettings.vue'),
            meta: {
                permission: Permission.VIEW_ACCOUNT_SETTINGS,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.AccountSettings'),
                        page: PageName.MY_ACCOUNT_ACCOUNT_SETTINGS
                    }
                ]
            }
        },
        {
            path: 'address-book',
            name: PageName.MY_ACCOUNT_ADDRESS_BOOK,
            component: () => import(
                '@/project/app/client-pages/my-account/address-book/AddressBookPage.vue'),
            meta: {
                permission: Permission.VIEW_ADDRESSBOOK,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.AddressBook'),
                        page: PageName.MY_ACCOUNT_ADDRESS_BOOK
                    }
                ]
            }
        },
        {
            path: 'administration',
            name: PageName.MY_ACCOUNT_USER_ADMINISTRATION,
            component: () => import(
                '@/project/app/client-pages/my-account/administration/UserAdministration.vue'),
            meta: {
                permission: Permission.MANAGE_ASSOCIATED_USER,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.UserAdministration'),
                        page: PageName.MY_ACCOUNT_USER_ADMINISTRATION
                    }
                ]
            }
        },
        {
            path: 'impersonation',
            name: PageName.MY_ACCOUNT_IMPERSONATION,
            component: () => import(
                '@/project/app/client-pages/my-account/impersonation/Impersonate.vue'),
            meta: {
                permission: Permission.IMPERSONATE,
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Impersonation'),
                        page: PageName.MY_ACCOUNT_IMPERSONATION
                    }
                ]
            }
        },
        {
            path: 'productguideconfiguration',
            name: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION,
            component: () => import(
                '@/project/app/client-pages/my-account/product-guide-configuration/ProductGuideConfigurationOverview.vue'),
            meta: {
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration'),
                        page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION
                    }
                ]
            }
        },
        {
            path: 'productguideconfiguration/edit',
            name: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION_NEW,
            component: () => import(
                '@/project/app/client-pages/my-account/product-guide-configuration/ProductGuideConfigurationEdit.vue'),
            meta: {
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration'),
                        page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration.Edit'),
                        page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION_NEW
                    }
                ]
            }
        },
        {
            path: 'productguideconfiguration/edit/:externalId',
            name: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION_EDIT,
            component: () => import(
                '@/project/app/client-pages/my-account/product-guide-configuration/ProductGuideConfigurationEdit.vue'),
            props: (route) => ({
                externalId: route.params.externalId
            }),
            meta: {
                headMeta: {},
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration'),
                        page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration.Edit'),
                        page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION_EDIT
                    }
                ]
            }
        },
        {
            path: 'rackconfigurations',
            name: PageName.MY_ACCOUNT_RACK_CONFIGURATIONS,
            component: () => import(
                '@/project/app/client-pages/my-account/rack-configurations/MyAccountRackConfigurations.vue'),
            meta: {
                headMeta: {},
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.RackConfigurations'),
                        page: PageName.MY_ACCOUNT_RACK_CONFIGURATIONS
                    }
                ]
            }
        },
        {
            path: 'next-delivery',
            name: PageName.MY_ACCOUNT_NEXT_DELIVERY,
            component: () => import(
                '@/project/app/client-pages/next-delivery-administration/NextDeliveryCountrySelect.vue'),
            meta: {
                headMeta: {},
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    }
                ],
                isPublic: false,
                hideBreadcrumb: false
            }
        },
        {
            path: 'next-delivery/:countryId',
            name: PageName.MY_ACCOUNT_NEXT_DELIVERY_COUNTRY,
            props: (route) => ({
                countryId: route.params.countryId
            }),
            component: () => import(
                '@/project/app/client-pages/next-delivery-administration/NextDeliveryOverview.vue'),
            meta: {
                headMeta: {},
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    }
                ],
                isPublic: false,
                hideBreadcrumb: false
            }
        },
        {
            path: 'lists',
            name: PageName.MY_ACCOUNT_PRODUCT_LISTS,
            component: () => import(
                '@/project/app/client-pages/my-account/product-lists/ProductAssortment.vue'),
            meta: {
                headMeta: {
                    seoTitle: dictionaryService.get('MyAccount.Navigation.AssortmentLists')
                },
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.AssortmentLists'),
                        page: PageName.MY_ACCOUNT_PRODUCT_LISTS
                    }
                ]
            }
        },
        {
            path: 'partner-programs',
            name: PageName.MY_ACCOUNT_PARTNER_PROGRAMS,
            component: () => import(
                '@/project/app/client-pages/my-account/partner-programs/PartnerProgramsPage.vue'),
            meta: {
                headMeta: {
                    seoTitle: dictionaryService.get('MyAccount.Navigation.PartnerPrograms')
                },
                breadcrumb: [
                    {
                        name: dictionaryService.get('MyAccount.Navigation.Title'),
                        page: PageName.MY_ACCOUNT
                    },
                    {
                        name: dictionaryService.get('MyAccount.Navigation.PartnerPrograms'),
                        page: PageName.MY_ACCOUNT_PARTNER_PROGRAMS
                    }
                ]
            }
        }
    ]
};

export function permissionForRoute(name: string): Permission | undefined {
    const route = MyAccountRoutes.name === name ? MyAccountRoutes : MyAccountRoutes.children!.find(r => r.name === name);
    return route ? route.meta?.permission as unknown as Permission : undefined;
}
